import { AppThunk } from "@app/redux/store/root";
import {
    SELECTED_VERSIONS_UPDATED,
    VERSIONS_FETCHED,
    APP_LIST,
    SELECTED_APP,
    COUNTRIES_FETCHED,
    SELECTED_COUNTRIES_UPDATED,
    START_DATE_FETCHED,
    SELECTED_DATE_RANGE_UPDATED,
    DEVELOPER_COUNTRY_DISTRIBUTION,
    RESET_DEV_ANALYTICS_FILTERS,
    CHART_DATA_FETCHED,
} from "./types";
import {
    fetchAppInstallsForDeveloperApi,
    fetchVersionForDeveloperAppApi,
    fetchCountriesForDeveloperAppApi,
    fetchStartDateForSelectedAppApi,
    fetchCountryDistributionApi,
    fetchChartDataApi,
} from "@app/redux/services/devAnalytics";
import TokenService from "@app/redux/services/token";

export const fetchAppsList =
    (token: string): AppThunk =>
    async (dispatch): Promise<any> => {
        try {
            const response = await fetchAppInstallsForDeveloperApi(token);
            dispatch({
                type: APP_LIST,
                payload: response.data,
            });
        } catch (err: any) {
            return Promise.reject(err);
        }
    };

export const fetchVersions =
    (token: string, appId: string): AppThunk =>
    async (dispatch): Promise<any> => {
        try {
            if (appId) {
                const response = await fetchVersionForDeveloperAppApi(token, appId);
                dispatch({
                    type: VERSIONS_FETCHED,
                    payload: response,
                });
            }
        } catch (err: any) {
            return Promise.reject(err);
        }
    };

interface SelectedVersion {
    id: string;
    name: string;
}

export const updateSelectedVersions =
    (newSelectedVersions: Array<SelectedVersion> | null): AppThunk =>
    async (dispatch): Promise<any> => {
        dispatch({
            type: SELECTED_VERSIONS_UPDATED,
            payload: newSelectedVersions,
        });
    };

interface SelectedApp {
    id: string;
    name: string;
    installsCount: number;
}
export const updateSelectedApp =
    (newSelectedApp: SelectedApp): AppThunk =>
    async (dispatch): Promise<any> => {
        dispatch({
            type: SELECTED_APP,
            payload: newSelectedApp,
        });
        dispatch({ type: RESET_DEV_ANALYTICS_FILTERS });
    };

export const fetchCountries =
    (token: string, appId: string): AppThunk =>
    async (dispatch): Promise<any> => {
        try {
            if (appId) {
                const response = await fetchCountriesForDeveloperAppApi(token, appId);
                dispatch({
                    type: COUNTRIES_FETCHED,
                    payload: response,
                });
            }
        } catch (err: any) {
            return Promise.reject(err);
        }
    };

interface SelectedCountry {
    id: string;
    name: string;
}

export const updateSelectedCountries =
    (newSelectedCountries: Array<SelectedCountry> | null): AppThunk =>
    async (dispatch): Promise<any> => {
        dispatch({
            type: SELECTED_COUNTRIES_UPDATED,
            payload: newSelectedCountries,
        });
    };

export const fetchStartDateForSelectedApp =
    (token: string, appId: string): AppThunk =>
    async (dispatch): Promise<any> => {
        try {
            if (appId) {
                const response = await fetchStartDateForSelectedAppApi(token, appId);
                dispatch({
                    type: START_DATE_FETCHED,
                    payload: response,
                });
            }
        } catch (err: any) {
            return Promise.reject(err);
        }
    };

export const fetchDeveloperCountryDistribution =
    (applicationId: string): AppThunk =>
    async (dispatch): Promise<any> => {
        try {
            const response = await fetchCountryDistributionApi(
                TokenService.getLocalAccessToken(),
                applicationId
            );

            dispatch({
                type: DEVELOPER_COUNTRY_DISTRIBUTION,
                payload: response,
            });
        } catch (err: any) {
            return Promise.reject(err);
        }
    };

export const updateSelectedDateRange =
    (newSelectedDateRange: Array<Date>, selectedPreDefinedDateRange: string): AppThunk =>
    async (dispatch): Promise<any> => {
        dispatch({
            type: SELECTED_DATE_RANGE_UPDATED,
            payload: { newSelectedDateRange, selectedPreDefinedDateRange },
        });
    };

export const resetDevAnalyticsFilters =
    (): AppThunk =>
    async (dispatch): Promise<any> => {
        dispatch({
            type: RESET_DEV_ANALYTICS_FILTERS,
        });
    };

export const fetchChartData =
    (
        appId: string,
        versions: Array<SelectedVersion> | null,
        countries: Array<SelectedCountry> | null,
        dateRange: Array<Date>,
        token: string,
        view?: string | null
    ): AppThunk =>
    async (dispatch): Promise<any> => {
        try {
            if (
                (countries && countries.length === 0) ||
                (versions && versions.length === 0) ||
                !appId
            ) {
                dispatch({
                    type: CHART_DATA_FETCHED,
                    payload: { data: { data: [] } },
                });
            } else {
                const response = await fetchChartDataApi(
                    appId,
                    versions,
                    countries,
                    dateRange,
                    token,
                    view
                );
                dispatch({
                    type: CHART_DATA_FETCHED,
                    payload: response,
                });
            }
        } catch (err: any) {
            return Promise.reject(err);
        }
    };
